import { useState, type FormEvent } from "react";
import {
  FieldRenderer,
  type BagRow,
  type BagValue,
  type CodeRefs,
  type FieldBag,
} from "@/components/submissions/FieldRenderer";
import {
  withFloorRequiredItems,
  type GroupItemField,
  type ManifestField,
  type MissingField,
  type ProductManifest,
} from "@/lib/brokerProducts";
import { missingFieldsOf } from "@/lib/submissions/brokerSubmissions";
import type { UnmatchedCode } from "@/lib/submissions/documentPrefill";
import { messageFor } from "@/lib/errors";
import { localTodayIso } from "@/lib/dates";
import {
  ExtractedMarker,
  ExtractedReviewBanner,
} from "@/components/ExtractedReview";
import { useExtractedReview } from "@/lib/useExtractedReview";

import { buttonClass } from "@/components/buttonStyles";
// BRK-S052 — the manifest-driven details step, replacing the WC-only rating
// form. Fully driven by the product manifest (no product conditionals):
// - Renders ONLY the submission-scoped INDICATION-FLOOR fields by default —
//   minimum information at every step. Account-scoped floor entries (mailing
//   address, legal name, …) live on the Account and are covered by the
//   account-floor step (BRK-S046), so they never render here.
// - The remaining (optional) manifest fields sit behind an explicit "Add more
//   detail" disclosure, collapsed by default — the account-form floor pattern
//   applied to submission details.
// - Submit validates the floor + complete group rows with handled inline
//   messages, then hands the FIELD BAG to the caller (which PUTs /details).
export function ManifestDetailsForm({
  manifest,
  submissionId,
  initialBag,
  carriedKeys,
  extractedKeys,
  unmatchedCodes,
  codeRefs,
  onSubmit,
}: {
  manifest: ProductManifest;
  /** The submission being rated — scopes the class-code typeahead (PMS-S156/BRK-S065). */
  submissionId: string;
  /** Prefill from the submission's existing field bag (renewals / resumed drafts). */
  initialBag?: Record<string, unknown>;
  /** Resolved class-code display refs (PMS-S150/BRK-S062) so prefilled classCode
   *  ids render as code + description, not the raw uuid. */
  codeRefs?: CodeRefs;
  /** The bag keys carried forward from the prior submission on a RENEWAL
   *  (BRK-S055/PMS-S148). Carried fields get a per-field marker and the form
   *  leads with a one-shot "Looks right" review confirmation — carried data
   *  reads as "please confirm", never as freshly entered. Term dates are never
   *  carried, so the effective date stays blank and required. */
  carriedKeys?: string[];
  /** The bag keys prefilled from an uploaded document (BRK-S038). Like
   *  `carriedKeys` these get a per-field marker + a review banner, but the marker
   *  reads "from your document" and CLEARS when the field is edited (a document
   *  value is a first draft to verify, not a confirmed prior term). Nothing
   *  auto-commits — the Save button is the explicit confirmation. */
  extractedKeys?: string[];
  /** Extracted class-code strings that didn't resolve to a canonical code
   *  (BRK-S038). Surfaced as a resolution notice so the broker picks the match in
   *  the exposures typeahead below — never silently dropped. */
  unmatchedCodes?: UnmatchedCode[];
  /** Persist the bag; throwing surfaces the message inline (NN #7). */
  onSubmit: (bag: FieldBag) => Promise<void>;
}) {
  // Group items a floor names (the WC employee counts) are asked on every row.
  const submissionFields = withFloorRequiredItems(manifest).fields.filter(
    (f) => f.scope === "submission",
  );
  const floorKeys = new Set(manifest.floors.indication);
  const floorFields = submissionFields.filter((f) => floorKeys.has(f.key));
  const optionalFields = submissionFields.filter((f) => !floorKeys.has(f.key));
  const carried = new Set(carriedKeys ?? []);
  // BRK-S038 — document-extracted fields are marked until edited (clear-on-edit).
  const review = useExtractedReview(extractedKeys);

  const [bag, setBag] = useState<FieldBag>(() =>
    seedBag(submissionFields, initialBag),
  );
  const [showOptional, setShowOptional] = useState(
    // Carried / extracted optional values should be visible for review, not
    // hidden behind the disclosure.
    () =>
      (carriedKeys ?? []).some((k) => !floorKeys.has(k)) ||
      (extractedKeys ?? []).some((k) => !floorKeys.has(k)),
  );
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Structured server field errors (PMS-S144 shape) — rendered as a per-field
  // list instead of a raw banner (BRK-S061), e.g. the server's past-effective-
  // date rejection.
  const [fieldErrors, setFieldErrors] = useState<MissingField[] | null>(null);

  function setField(key: string, value: BagValue | undefined) {
    review.touch(key);
    setBag((b) => {
      const next = { ...b };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const cleaned = cleanBag(bag, submissionFields);
    const problem =
      floorError(floorFields, cleaned) ??
      effectiveDateError(submissionFields, cleaned);
    if (problem) {
      setError(problem);
      setFieldErrors(null);
      return;
    }
    setPending(true);
    setError(null);
    setFieldErrors(null);
    try {
      await onSubmit(cleaned);
    } catch (err) {
      // A structured field error (PMS-S144 shape — e.g. the server's own
      // past-effective-date rejection) renders as a per-field list; anything
      // else falls back to the single handled message (NN #7).
      const missing = missingFieldsOf(err);
      if (missing) setFieldErrors(missing);
      else setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
      <div className="flex flex-col">
        <h2 className="text-heading text-brk-text">
          Rating details ({manifest.label})
        </h2>
        <p className="text-sm text-brk-dim">
          Add the minimum we need to get a price indication.
        </p>
      </div>
      <ExtractedReviewBanner count={review.pendingCount} />
      {unmatchedCodes && unmatchedCodes.length > 0 && (
        <div
          role="status"
          className="flex flex-col gap-1 rounded-md border border-brk-accent/40 bg-brk-bg px-3 py-2 text-sm text-brk-dim"
        >
          <span className="font-medium text-brk-text">
            {unmatchedCodes.length === 1
              ? "1 class code from your document needs a match"
              : `${unmatchedCodes.length} class codes from your document need a match`}
          </span>
          <span>
            We read{" "}
            {unmatchedCodes.map((c, i) => (
              <span key={`${c.path}-${i}`}>
                {i > 0 ? ", " : ""}
                <span className="font-medium text-brk-text">“{c.raw}”</span>
              </span>
            ))}{" "}
            but couldn’t match {unmatchedCodes.length === 1 ? "it" : "them"} to
            a class code — pick the match in the payroll rows below.
          </span>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {fieldErrors && (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded-md border border-brk-danger/40 bg-brk-danger/5 px-3 py-2 text-sm text-brk-text"
        >
          <p className="font-medium">Please fix these fields:</p>
          <ul className="list-inside list-disc text-brk-dim">
            {fieldErrors.map((f) => (
              <li key={f.key}>{f.label}</li>
            ))}
          </ul>
        </div>
      )}
      {carried.size > 0 && !reviewConfirmed && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim"
        >
          <span>
            We carried these details forward from the prior submission — review
            and adjust anything that changed.
          </span>
          <button
            type="button"
            onClick={() => setReviewConfirmed(true)}
            className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
          >
            Looks right
          </button>
        </div>
      )}
      {carried.size > 0 && reviewConfirmed && (
        <p role="status" className="text-sm text-brk-dim">
          Now set the fresh effective date for the new term below.
        </p>
      )}
      <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
        {floorFields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1">
            <FieldRenderer
              field={field}
              value={bag[field.key]}
              onChange={(v) => setField(field.key, v)}
              required
              codeRefs={codeRefs}
              submissionId={submissionId}
            />
            {carried.has(field.key) && <CarriedMarker />}
            {review.isPending(field.key) && <ExtractedMarker />}
          </div>
        ))}

        {optionalFields.length > 0 && !showOptional && (
          <button
            type="button"
            onClick={() => setShowOptional(true)}
            className="self-start text-sm text-brk-accent hover:underline"
          >
            + Add more detail
          </button>
        )}
        {showOptional && (
          <div className="flex flex-col gap-4 border-t border-brk-border pt-4">
            <p className="text-sm text-brk-dim">
              Optional details — more information can sharpen the indication.
            </p>
            {optionalFields.map((field) => (
              <div key={field.key} className="flex flex-col gap-1">
                <FieldRenderer
                  field={field}
                  value={bag[field.key]}
                  onChange={(v) => setField(field.key, v)}
                  codeRefs={codeRefs}
                  submissionId={submissionId}
                />
                {carried.has(field.key) && <CarriedMarker />}
                {review.isPending(field.key) && <ExtractedMarker />}
              </div>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={pending}
          className={`${buttonClass()} self-start`}
        >
          {pending ? "Saving…" : "Save details"}
        </button>
      </form>
    </div>
  );
}

// The subtle per-field hint distinguishing a carried-forward value from a
// freshly entered one (BRK-S055).
function CarriedMarker() {
  return (
    <p className="text-xs text-brk-dim">
      <span aria-hidden="true">↺ </span>Carried forward — confirm or edit.
    </p>
  );
}

// Narrow an unknown prefill bag to the manifest's submission fields — anything
// the manifest doesn't declare is dropped (the server owns unknown keys).
function seedBag(
  fields: ManifestField[],
  initial: Record<string, unknown> | undefined,
): FieldBag {
  if (!initial) return {};
  const bag: FieldBag = {};
  for (const field of fields) {
    const value = initial[field.key];
    if (value !== undefined && value !== null)
      bag[field.key] = value as BagValue;
  }
  return bag;
}

// Drop empty group rows (an untouched trailing row isn't data) so the emitted
// bag carries exactly what the broker entered.
function cleanBag(bag: FieldBag, fields: ManifestField[]): FieldBag {
  const next: FieldBag = {};
  for (const field of fields) {
    const value = bag[field.key];
    if (value === undefined) continue;
    if (field.type === "group" && Array.isArray(value)) {
      const rows = (value as BagRow[]).filter((row) =>
        Object.values(row).some((v) => v !== undefined),
      );
      if (rows.length) next[field.key] = rows;
      continue;
    }
    next[field.key] = value;
  }
  return next;
}

// The coverage start date must be today or later (BRK-S061). Date-field aware —
// keyed to the canonical `effectiveDate` field, so it holds for any manifest
// that declares one, not just WC. Presence is enforced by floorError; this only
// rejects a past date once one is entered. ISO date strings compare correctly
// lexicographically. The server enforces the same rule (its structured
// rejection renders via missingFieldsOf).
function effectiveDateError(
  fields: ManifestField[],
  bag: FieldBag,
): string | null {
  const field = fields.find(
    (f) => f.key === "effectiveDate" && f.type === "date",
  );
  if (!field) return null;
  const value = bag[field.key];
  if (typeof value !== "string" || value === "") return null;
  if (value < localTodayIso()) return `${field.label} must be today or later.`;
  return null;
}

// First-missing floor validation, mirroring the account-floor pattern: one
// handled message naming the field. Group fields additionally block PARTIAL
// rows (a data-entry mistake, never silently dropped — BRK-S047).
function floorError(
  floorFields: ManifestField[],
  bag: FieldBag,
): string | null {
  for (const field of floorFields) {
    const value = bag[field.key];
    if (field.type === "group") {
      const rows = Array.isArray(value) ? (value as BagRow[]) : [];
      const requiredKeys = (field.item ?? [])
        .filter((i: GroupItemField) => !i.optional)
        .map((i) => i.key);
      const partial = rows.some((row) => {
        const filled = requiredKeys.filter((k) => row[k] !== undefined);
        return filled.length > 0 && filled.length < requiredKeys.length;
      });
      if (partial)
        return `Complete each ${field.label.toLowerCase()} row — or remove it.`;
      const complete = rows.filter((row) =>
        requiredKeys.every((k) => row[k] !== undefined),
      );
      if (complete.length < (field.minItems ?? 1))
        return `Add at least ${field.minItems ?? 1} ${field.label.toLowerCase()} row${(field.minItems ?? 1) > 1 ? "s" : ""}.`;
      continue;
    }
    if (value === undefined || value === "")
      return `Enter ${field.label.toLowerCase()}.`;
  }
  return null;
}
